import React, { useMemo } from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import { Icon, IconName } from '../../../atoms/Icon'

interface DataFreshnessIndicatorProps {
  timestamp: Date | string
  t: TFunction
  showIcon?: boolean
  className?: string
}

type FreshnessLevel = 'recent' | 'aging' | 'outdated'

interface FreshnessInfo {
  level: FreshnessLevel
  color: string
  icon: IconName
  label: string
}

const Container = styled.span<{ $level: FreshnessLevel }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ $level, theme }) => {
    switch ($level) {
      case 'recent':
        return theme.colors.success || '#10b981'
      case 'aging':
        return theme.colors.warning || '#f59e0b'
      case 'outdated':
        return theme.colors.danger || '#ef4444'
    }
  }};
`

const DateText = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

const AgeText = styled.span`
  opacity: 0.8;
`

function getFreshnessInfo(
  timestamp: Date | string,
  t: TFunction
): FreshnessInfo {
  const lastUpdate = new Date(timestamp)
  const now = new Date()
  const yearsDiff =
    (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24 * 365)

  if (yearsDiff < 5) {
    return {
      level: 'recent',
      color: '#10b981',
      icon: 'check-circle',
      label: t('freshness.levels.recent'),
    }
  }
  if (yearsDiff < 10) {
    return {
      level: 'aging',
      color: '#f59e0b',
      icon: 'alert-circle',
      label: t('freshness.levels.aging'),
    }
  }
  return {
    level: 'outdated',
    color: '#ef4444',
    icon: 'alert-triangle',
    label: t('freshness.levels.outdated'),
  }
}

function formatTimestamp(timestamp: Date | string): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

function getYearsAgo(timestamp: Date | string, t: TFunction): string {
  const lastUpdate = new Date(timestamp)
  const now = new Date()
  const yearsDiff = Math.floor(
    (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24 * 365)
  )

  if (yearsDiff === 0) return t('freshness.thisYear')
  if (yearsDiff === 1) return t('freshness.oneYearAgo')
  return t('freshness.yearsAgo', { count: yearsDiff })
}

export const DataFreshnessIndicator: React.FC<DataFreshnessIndicatorProps> = ({
  timestamp,
  t,
  showIcon = true,
  className,
}) => {
  const freshnessInfo = useMemo(
    () => getFreshnessInfo(timestamp, t),
    [timestamp, t]
  )
  const formattedDate = useMemo(() => formatTimestamp(timestamp), [timestamp])
  const yearsAgo = useMemo(() => getYearsAgo(timestamp, t), [timestamp, t])

  return (
    <Container $level={freshnessInfo.level} className={className}>
      {showIcon && <Icon name={freshnessInfo.icon} size="small" />}
      <DateText>{formattedDate}</DateText>
      <AgeText>({yearsAgo})</AgeText>
    </Container>
  )
}
